import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { AlertMessage2 } from 'components/AlertMessage2';
import { getStaffScheduleByDate, getStaffScheduleByWeekDay } from 'components/StaffSchedule/utils/StaffScheduleUtils';
import { useMarketplace, useTimeZone } from 'hooks';
import { WeekDay } from 'model';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { warningsForActiveAssignments } from 'model/ServiceSession';

export interface SessionConflictWarningsProps {
    staff: Array<Staff>;
    weekDays?: Array<WeekDay>;
    date?: Moment;
    assignments: Array<{ staffId?: number; time?: string }>;
    extraWarnings?: Array<string>;
}

const useStyles = makeStyles(() =>
    createStyles({
        alert: {
            'margin': 0,

            '& p': {
                fontSize: 16,
                lineHeight: 1.4,
                paddingRight: 10
            }
        }
    })
);

export const SessionScheduleAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const classes = useStyles();

    return (
        <AlertMessage2
            icon={faCircleInfo}
            bgColor="#F8F5F1"
            bdColor="#BCB8AE"
            className={classes.alert}
        >
            {children}
        </AlertMessage2>
    );
};

export const SessionConflictWarnings: React.FC<SessionConflictWarningsProps> = props => {
    const timeZone = useTimeZone();
    const marketplace = useMarketplace();

    const warnings = React.useMemo(() => {
        const messages = new Map<string, string>();
        const weekDays = props.date ? [props.date.format('dddd').toLowerCase() as WeekDay] : props.weekDays ?? [];

        // The backend sends the same text with a straight apostrophe, so raw strings never match.
        const add = (message: string) => {
            const key = message.replace(/[\u2018\u2019]/g, "'");
            if (!messages.has(key)) messages.set(key, message);
        };

        weekDays.forEach(weekDay => {
            props.assignments.forEach(assignment => {
                const member = props.staff.find(item => item.id === assignment.staffId);

                if (!member || !assignment.time) return;

                const schedule = props.date
                    ? getStaffScheduleByDate(marketplace, member.schedule, props.date.clone(), timeZone)
                    : getStaffScheduleByWeekDay(marketplace, member.schedule, weekDay, timeZone);
                const time = moment(assignment.time, 'HH:mm');
                const isOutside =
                    time.isBefore(moment(schedule.from.format('HH:mm'), 'HH:mm')) ||
                    time.isAfter(moment(schedule.to.format('HH:mm'), 'HH:mm'));

                if (schedule.isBusinessClosed || isOutside) {
                    add('Date/Time outside business hours');
                }

                if (!schedule.isStaffAvailable || isOutside) {
                    add(`Conflicts with ${member.person.firstName} ${member.person.lastName}’s working hours.`);
                }
            });
        });

        const activeStaffNames = props.assignments.flatMap(assignment => {
            const member = props.staff.find(item => item.id === assignment.staffId);
            return member ? [`${member.person.firstName} ${member.person.lastName}`] : [];
        });

        warningsForActiveAssignments(props.extraWarnings ?? [], activeStaffNames).forEach(add);

        return Array.from(messages.values());
    }, [props.weekDays, props.date, props.assignments, props.staff, props.extraWarnings, marketplace, timeZone]);

    if (warnings.length === 0) return null;

    return (
        <>
            {warnings.map(warning => (
                <SessionScheduleAlert key={warning}>
                    <Typography>{warning}</Typography>
                </SessionScheduleAlert>
            ))}
        </>
    );
};
